import React, { useEffect, useState, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { Icon, Divider } from 'semantic-ui-react';
import SidebarContext from 'context/SidebarContext';
import SessionContext from 'context/SessionContext';
import { defaultQuickAccessItems, categoryMenuItems, getMenuItemByPath, allMenuItems } from '../config/menuItems';
import { getFavorites } from '../utils/favoriteUtils';
import SearchBar from './SearchBar';
import SidebarItem from './SidebarItem';
import SidebarCategory from './SidebarCategory';

function Sidebar({ location }) {
  const { isOpen } = React.useContext(SidebarContext);
  const session = React.useContext(SessionContext);
  const [search, setSearch] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [refreshFavorites, setRefreshFavorites] = useState(0);

  // Cargar favoritos desde localStorage
  useEffect(() => {
    const favoritePaths = getFavorites();
    const favoriteMenuItems = favoritePaths
      .map(path => getMenuItemByPath(path))
      .filter(item => item && (!item.can || session.can(item.can)));

    setFavoriteItems(favoriteMenuItems);
  }, [session, refreshFavorites]);

  // Función para actualizar la lista de favoritos
  const handleFavoriteChange = () => {
    setRefreshFavorites(prev => prev + 1);
  };

  // Filtrar elementos de acceso rápido predeterminados basados en permisos
  const permittedQuickItems = useMemo(() => {
    return defaultQuickAccessItems.filter(item => !item.can || session.can(item.can));
  }, [session]);

  // Filtrar categorías basadas en permisos de sus elementos hijos
  const permittedCategories = useMemo(() => {
    return categoryMenuItems.map(category => {
      // Filtrar elementos de la categoría basados en permisos
      const permittedItems = category.items.filter(item =>
        !item.can || session.can(item.can)
      );

      return {
        ...category,
        items: permittedItems
      };
    }).filter(category => category.items.length > 0); // Solo mantener categorías con elementos permitidos
  }, [session]);

  // Estados filtrados basados en búsqueda y permisos
  const [filteredQuickItems, setFilteredQuickItems] = useState(permittedQuickItems);
  const [filteredFavoriteItems, setFilteredFavoriteItems] = useState(favoriteItems);
  const [filteredCategories, setFilteredCategories] = useState(permittedCategories);

  // Encontrar y expandir automáticamente la categoría que contiene la ruta actual
  useEffect(() => {
    if (!location || !location.pathname) return;

    // Si hay búsqueda activa, no cambiamos el estado de expansión
    if (search) return;

    // Buscar si la ruta actual está en alguna categoría permitida (incluyendo subitems)
    for (let category of permittedCategories) {
      const foundItem = category.items.find(item => {
        // Verificar si la ruta coincide con el item principal
        if (location.pathname === item.path || location.pathname.indexOf(`${item.path}/`) > -1) {
          return true;
        }
        
        // Verificar si la ruta coincide con algún subitem
        if (item.subItems && item.subItems.length > 0) {
          return item.subItems.some(subItem => {
            const subItemPath = typeof subItem === 'object' ? subItem.path : subItem;
            return location.pathname === subItemPath || location.pathname.indexOf(`${subItemPath}/`) > -1;
          });
        }
        
        return false;
      });

      if (foundItem) {
        setExpandedCategory(category.category);
        break;
      }
    }
  }, [location, permittedCategories, search]);

  // Filtrar menú cuando se busca
  useEffect(() => {
    if (!search) {
      setFilteredQuickItems(permittedQuickItems);
      setFilteredFavoriteItems(favoriteItems);
      setFilteredCategories(permittedCategories);
      return;
    }

    // Filtrar elementos de acceso rápido predeterminados
    const filteredQuick = permittedQuickItems.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase())
    );

    // Filtrar elementos favoritos
    const filteredFavs = favoriteItems.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase())
    );

    // Filtrar categorías y sus elementos (incluyendo subitems)
    const filtered = permittedCategories.map(category => {
      const filteredItems = category.items.map(item => {
        // Buscar en el label del item principal
        const matchesLabel = item.label.toLowerCase().includes(search.toLowerCase());
        
        // Buscar en subitems si existen
        if (item.subItems && item.subItems.length > 0) {
          const hasMatchingSubItem = item.subItems.some(subItem => {
            const subItemLabel = typeof subItem === 'object' ? subItem.label : subItem;
            return subItemLabel && subItemLabel.toLowerCase().includes(search.toLowerCase());
          });
          
          // Si coincide el label principal o algún subitem, incluir el item
          if (matchesLabel || hasMatchingSubItem) {
            // Filtrar subitems que coincidan
            if (hasMatchingSubItem) {
              return {
                ...item,
                subItems: item.subItems.filter(subItem => {
                  const subItemLabel = typeof subItem === 'object' ? subItem.label : subItem;
                  return subItemLabel && subItemLabel.toLowerCase().includes(search.toLowerCase());
                })
              };
            }
            return item;
          }
          return null;
        }
        
        return matchesLabel ? item : null;
      }).filter(item => item !== null);

      return {
        ...category,
        items: filteredItems
      };
    }).filter(category => category.items.length > 0);

    setFilteredQuickItems(filteredQuick);
    setFilteredFavoriteItems(filteredFavs);
    setFilteredCategories(filtered);

    // Si hay búsqueda, expandimos solo la primera categoría con resultados
    if (search && filtered.length > 0) {
      setExpandedCategory(filtered[0].category);
    }
  }, [search, permittedQuickItems, permittedCategories, favoriteItems]);

  function clearSearch() {
    setSearch('');
  }

  function toggleCategory(categoryName) {
    // Si la categoría ya está expandida, la colapsamos
    if (expandedCategory === categoryName) {
      setExpandedCategory(null);
    } else {
      // Si no, colapsamos la anterior y expandimos la nueva
      setExpandedCategory(categoryName);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        scrollbarWidth: '0px',
        backgroundColor: '#1849a0',
      }}
    >
      <div className='amber-sidebar'>
        <h1 style={{ padding: '16px', margin: '0', color: 'white' }}>Amber</h1>

        <SearchBar
          placeholder='Buscar...'
          value={search}
          onChange={setSearch}
        />

        {/* Sección de favoritos - solo se muestra si hay favoritos */}
        {filteredFavoriteItems.length > 0 && (
          <div className="favorites-section">
            <div className="sidebar-section-title" style={{ padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '0.9em' }}>
              <Icon name="star" style={{ color: '#FFD700', marginRight: '5px' }} />
              FAVORITOS
            </div>
            {filteredFavoriteItems.map((item, i) => (
              <SidebarItem
                {...item}
                key={`fav-${item.path}`}
                clearSearch={clearSearch}
                onFavoriteChange={handleFavoriteChange}
              />
            ))}
          </div>
        )}

        {/* Mostrar divisor si hay favoritos y otros elementos */}
        {filteredFavoriteItems.length > 0 && (filteredQuickItems.length > 0 || filteredCategories.length > 0) && (
          <Divider style={{ margin: '8px 16px', borderColor: 'rgba(255,255,255,0.1)' }} />
        )}

        {/* Sección de acceso rápido predeterminado */}
        {filteredQuickItems.length > 0 && (
          <div className="quick-access-section">
            <div className="sidebar-section-title" style={{ padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '0.9em' }}>
              ACCESO RÁPIDO
            </div>
            {filteredQuickItems.map((item, i) => (
              <SidebarItem
                {...item}
                key={`quick-${item.path}`}
                clearSearch={clearSearch}
                onFavoriteChange={handleFavoriteChange}
              />
            ))}
          </div>
        )}

        {/* Mostrar divisor entre acceso rápido y categorías */}
        {filteredQuickItems.length > 0 && filteredCategories.length > 0 && (
          <Divider style={{ margin: '8px 16px', borderColor: 'rgba(255,255,255,0.1)' }} />
        )}

        {/* Sección de menús categorizados */}
        {filteredCategories.length > 0 && (
          <div className="categories-section">
            <div className="sidebar-section-title" style={{ padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '0.9em' }}>
              MÓDULOS
            </div>
            {filteredCategories.map((category, i) => (
              <SidebarCategory
                key={`cat-${category.category}`}
                name={category.category}
                icon={category.icon}
                items={category.items}
                isExpanded={expandedCategory === category.category}
                toggleExpand={() => toggleCategory(category.category)}
                clearSearch={clearSearch}
                onFavoriteChange={handleFavoriteChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default withRouter(Sidebar);